import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Bell,
  ChevronDown,
  Settings as SettingsIcon,
  LogOut,
  Sparkles,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Bug,
  Inbox,
  Search,
} from 'lucide-react'
import { Breadcrumbs, type BreadcrumbItem } from '../../components/ui/Breadcrumbs'
import GlobalSearch from '../../components/GlobalSearch'
import TourHelpButton from '../../components/TourHelpButton'
import ThemeToggle from '../../components/ThemeToggle'
import BackgroundJobsWidget from '../../components/BackgroundJobsWidget'
import JobProgressModal from '../../components/JobProgressModal'
import PriceIndexJobProgressModal from '../../components/PriceIndexJobProgressModal'
import {
  isPublishCardsJob,
  PublishJobModalSlot,
} from '../../components/marketplace-publish'
import { useAuthStore } from '../../stores/authStore'
import { type BackgroundJob, useBackgroundJobsStore } from '../../stores/backgroundJobsStore'
import { NotificationPanel } from '../../features/notifications/NotificationPanel'
import { resolveRouteMetadata, sectionPath } from '../router'
import { cn } from '../../lib/cn'
import BugReportModal from '../../features/work-items/BugReportModal'
import { hasPermission } from '../access'
import { jobsAPI } from '../../lib/api'

const NO_PERMISSIONS: string[] = []

interface HeaderProps {
  /** Toggle desktop sidebar collapse */
  onToggleCollapse: () => void
  /** Open mobile sidebar drawer */
  onOpenMobileSidebar: () => void
  /** Current collapsed state */
  collapsed: boolean
  onShowWhatsNew?: () => void
}

export function Header({
  onToggleCollapse,
  onOpenMobileSidebar,
  collapsed,
  onShowWhatsNew,
}: HeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [selectedJob, setSelectedJob] = useState<BackgroundJob | null>(null)
  const [showJobModal, setShowJobModal] = useState(false)
  const [showBugReport, setShowBugReport] = useState(false)
  // На ширине 768–1023 px поиск свёрнут в кнопку, чтобы не перекрывать крошки (W4-15).
  const [showCompactSearch, setShowCompactSearch] = useState(false)
  const permissions = user?.permissions ?? NO_PERMISSIONS
  const canSearch = hasPermission('Product:READ', permissions)
  const canReportBug = hasPermission('Backlog:CREATE', permissions)
  const canSeeOwnReports = hasPermission('Backlog:READ_OWN', permissions)

  const jobsWidgetRef = useRef<HTMLDivElement>(null)

  const selectedJobFromStore = useBackgroundJobsStore((state) => {
    if (!selectedJob?.jobId) return null
    return state.jobs.get(selectedJob.jobId) || selectedJob
  })

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (showUserMenu && !target.closest('[data-user-menu]')) setShowUserMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const jobId = params.get('jobId')
    if (!jobId) return
    let cancelled = false
    const openLinkedJob = async () => {
      try {
        const existing = useBackgroundJobsStore.getState().jobs.get(jobId)
        const response = existing ? null : await jobsAPI.getJob(jobId)
        const raw = existing || response?.data?.data || response?.data
        if (!cancelled && raw?.jobId) {
          setSelectedJob(raw as BackgroundJob)
          setShowJobModal(true)
        }
      } catch (error) {
        console.error('Failed to open job from notification', error)
      } finally {
        if (!cancelled) {
          params.delete('jobId')
          const query = params.toString()
          navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true })
        }
      }
    }
    void openLinkedJob()
    return () => { cancelled = true }
  }, [location.pathname, location.search, navigate])

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const meta = resolveRouteMetadata(location.pathname)
    const crumbs: BreadcrumbItem[] = []
    if (meta) {
      if (meta.parent) {
        // Раздел — это группа меню без собственной страницы; ведём на первую
        // готовую страницу внутри неё, иначе крошка остаётся мёртвой.
        const parentPath = sectionPath(meta.parent)
        crumbs.push({
          label: meta.parent,
          href: parentPath,
          onClick: parentPath ? () => navigate(parentPath) : undefined,
        })
      }
      crumbs.push({ label: meta.label })
    } else {
      crumbs.push({ label: location.pathname.replace('/', '') || 'Дашборд' })
    }
    return crumbs
  }

  const iconBtn =
    'shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-md ' +
    'text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors ' +
    'dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-zinc-800 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-500'

  return (
    <>
      <header className="relative z-30 h-14 bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800 px-4 flex items-center gap-3 shrink-0">
        {/* Mobile: hamburger; Desktop: collapse toggle */}
        <button
          className={cn(iconBtn, 'lg:hidden')}
          onClick={onOpenMobileSidebar}
          aria-label="Открыть меню"
        >
          <Menu className="h-5 w-5" />
        </button>
        <button
          className={cn(iconBtn, 'hidden lg:inline-flex')}
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
        >
          {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
        </button>

        <div className="hidden md:block min-w-0">
          <Breadcrumbs
            items={getBreadcrumbs()}
            showHome={location.pathname !== '/'}
            onHomeClick={() => navigate('/')}
          />
        </div>

        {canSearch && (
          <div className="flex-1 max-w-[480px] hidden lg:block">
            <GlobalSearch onNavigate={(url) => navigate(url)} />
          </div>
        )}
        {canSearch && (
          <button
            className={cn(iconBtn, 'hidden md:inline-flex lg:hidden')}
            onClick={() => setShowCompactSearch((value) => !value)}
            aria-label="Поиск"
            aria-expanded={showCompactSearch}
            title="Поиск"
          >
            <Search className="h-5 w-5" />
          </button>
        )}

        <div className="ml-auto flex items-center gap-1">
          <TourHelpButton pathname={location.pathname} />
          <ThemeToggle />

          <BackgroundJobsWidget
            widgetRef={jobsWidgetRef}
            onJobClick={(job) => {
              setSelectedJob(job)
              setShowJobModal(true)
            }}
          />

          {onShowWhatsNew && (
            <button
              className={iconBtn}
              onClick={onShowWhatsNew}
              aria-label="Что нового"
              title="Что нового"
            >
              <Sparkles className="h-5 w-5" />
            </button>
          )}

          {canReportBug && (
            <button
              className={iconBtn}
              onClick={() => setShowBugReport(true)}
              aria-label="Сообщить об ошибке"
              title="Сообщить об ошибке"
            >
              <Bug className="h-5 w-5" />
            </button>
          )}

          {/* Notifications */}
          <NotificationPanel triggerClassName={iconBtn} />

          {/* User menu */}
          <div className="relative ml-1" data-user-menu>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 rounded-md hover:bg-gray-100 dark:hover:bg-zinc-800 px-1.5 py-1 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-xs font-semibold shrink-0">
                {user?.user_name?.charAt(0).toUpperCase()}
              </div>
              <div className="hidden md:block text-left min-w-0">
                <p className="text-xs font-semibold text-gray-900 dark:text-zinc-100 leading-tight truncate max-w-[120px]">
                  {user?.user_name}
                </p>
                <p className="text-2xs text-gray-500 dark:text-zinc-400 capitalize leading-tight">{user?.role}</p>
              </div>
              <ChevronDown className="hidden md:block h-4 w-4 text-gray-400 dark:text-zinc-500" />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-1.5 w-56 bg-white dark:bg-zinc-900 rounded-md shadow-medium border border-gray-200 dark:border-zinc-800 py-1 z-50">
                <div className="px-3 py-2 border-b border-gray-100 dark:border-zinc-800">
                  <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100 truncate">{user?.user_name}</p>
                  <p className="text-xs text-gray-500 dark:text-zinc-400 truncate">{user?.email}</p>
                  <p className="text-2xs text-gray-500 dark:text-zinc-400 capitalize mt-0.5">{user?.role}</p>
                </div>
                <button
                  onClick={() => { navigate('/settings'); setShowUserMenu(false) }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-zinc-200 hover:bg-gray-50 dark:hover:bg-zinc-800"
                >
                  <SettingsIcon className="h-4 w-4 text-gray-500 dark:text-zinc-400" />
                  Настройки
                </button>
                <button
                  onClick={() => { navigate('/notifications'); setShowUserMenu(false) }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-zinc-200 hover:bg-gray-50 dark:hover:bg-zinc-800"
                >
                  <Bell className="h-4 w-4 text-gray-500 dark:text-zinc-400" />
                  Уведомления
                </button>
                {canSeeOwnReports && (
                  <button
                    onClick={() => { navigate('/backlog/mine'); setShowUserMenu(false) }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-zinc-200 hover:bg-gray-50 dark:hover:bg-zinc-800"
                  >
                    <Inbox className="h-4 w-4 text-gray-500 dark:text-zinc-400" />
                    Мои обращения
                  </button>
                )}
                <div className="border-t border-gray-100 dark:border-zinc-800 mt-1 pt-1">
                  <button
                    onClick={() => { logout(); navigate('/login'); setShowUserMenu(false) }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40"
                  >
                    <LogOut className="h-4 w-4" />
                    Выход
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {canSearch && showCompactSearch && (
        <div className="relative z-30 hidden border-b border-gray-200 bg-white px-4 py-2 dark:border-zinc-800 dark:bg-zinc-900 md:block lg:hidden">
          <GlobalSearch
            onNavigate={(url) => {
              setShowCompactSearch(false)
              navigate(url)
            }}
          />
        </div>
      )}

      {/* Job Progress Modal */}
      {selectedJob && (
        // Выгрузка карточек показывает свой статус по каждой карточке в разрезе
        // кабинета — тот же компонент, что открывается сразу после запуска.
        isPublishCardsJob(selectedJob) ? (
          <PublishJobModalSlot
            key={selectedJob.jobId}
            job={selectedJob}
            isOpen={showJobModal}
            onClose={() => setShowJobModal(false)}
          />
        ) : selectedJob.type.startsWith('price_index_') ? (
          <PriceIndexJobProgressModal
            key={selectedJob.jobId}
            job={selectedJobFromStore || selectedJob}
            isOpen={showJobModal}
            onClose={() => setShowJobModal(false)}
          />
        ) : (
          <JobProgressModal
            key={selectedJob.jobId}
            job={selectedJobFromStore || selectedJob}
            isOpen={showJobModal}
            onClose={() => setShowJobModal(false)}
            widgetElementRef={jobsWidgetRef}
          />
        )
      )}

      {canReportBug && <BugReportModal isOpen={showBugReport} onClose={() => setShowBugReport(false)} />}
    </>
  )
}
